/** Launch Library 2 ticker: next launch with a 1 s countdown and the list of ten (loader-launches.js). */
import { PlayCircle } from 'lucide-react';
import { formatLaunchCountdown, LAUNCH_LIST_LIMIT, nextLaunch } from '../../../awareness/launches';
import { useAwareness, type LaunchSummary } from '../../../awareness/store';
import { fmtUtc } from '../../format';
import { ErrorLine, Muted, SubHeading } from './Section';
import { useNow } from './shared';

function Watch({ launch, large }: { launch: LaunchSummary; large?: boolean }) {
  if (launch.watchUrl) {
    return (
      <a
        href={launch.watchUrl}
        target="_blank"
        rel="noopener noreferrer"
        title="Watch live"
        className={large ? 'inline-flex items-center gap-1 text-accent hover:underline' : 'inline-flex items-center gap-1 text-[10px] text-accent hover:underline'}
      >
        <PlayCircle className="h-3 w-3" /> {large ? 'Watch live' : 'Watch'}
      </a>
    );
  }
  return (
    <span className="text-[10px] text-ink-3" title="Operator has not published a webcast URL yet">
      stream TBA
    </span>
  );
}

export function LaunchesPanel() {
  const state = useAwareness((s) => s.launches);
  const now = useNow(1000);
  if (!state) return <Muted>Loading launches.</Muted>;
  const next = nextLaunch(state.launches, now);
  return (
    <div className="flex flex-col gap-2">
      <div className="rounded-control border border-hairline bg-inset/60 px-3 py-2">
        <div className="text-[10px] uppercase tracking-wider text-ink-3">Next launch</div>
        {next ? (
          <>
            <div className="flex flex-wrap items-baseline gap-2 text-ink">
              <span className="font-medium">{next.name}</span>
              <Watch launch={next} large />
            </div>
            <div className="font-mono text-lg text-accent">
              T-{formatLaunchCountdown(((next.netMs ?? now) - now) / 1000)}
            </div>
            {next.padLocation && <div className="text-ink-3">{next.padLocation}</div>}
          </>
        ) : (
          <div className="text-ink-3">No upcoming</div>
        )}
      </div>
      {state.error && <ErrorLine text={`Launch feed unavailable: ${state.error}`} />}
      <SubHeading>Upcoming</SubHeading>
      {state.launches.length === 0 ? (
        <Muted>No upcoming launches</Muted>
      ) : (
        <ul className="flex flex-col gap-1.5">
          {state.launches.slice(0, LAUNCH_LIST_LIMIT).map((l) => (
            <li key={l.id} className="rounded-control border border-hairline px-2.5 py-1.5 leading-snug">
              <div className="flex flex-wrap items-baseline gap-2 text-ink">
                <span>{l.name}</span>
                <Watch launch={l} />
              </div>
              <div className="text-ink-3">
                {l.rocket}
                {l.padName ? `, ${l.padName}${l.padLocation ? `, ${l.padLocation}` : ''}` : ''}
              </div>
              <div className="font-mono text-[10px] text-ink-2">{l.netMs !== null ? fmtUtc(l.netMs) : 'TBD'}</div>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
